import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, Shield, Wrench, Clock, DollarSign } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function SaskatoonRepair() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How much does roof repair cost in Saskatoon?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Minor repairs in Saskatoon typically range from $300 to $800, while major repairs can range from $1,000 to $3,000. We provide fixed-price quotes before any work begins.",
        },
      },
      {
        "@type": "Question",
        name: "How quickly can you fix a leaking roof in Saskatoon?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We prioritize active leaks and storm damage. In most cases, we can have a repair crew out within 24-48 hours to secure your property and stop the water ingress.",
        },
      },
      {
        "@type": "Question",
        name: "Do you offer emergency roof repairs in the winter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, we provide year-round emergency roof repairs in Saskatoon, including during the winter months when ice damming is most prevalent.",
        },
      },
    ],
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": "https://www.saskroofingcompany.com/#localbusiness",
    name: "Sask Roofing Company",
    url: "https://www.saskroofingcompany.com/",
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: "Saskatoon",
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roof Repair",
    provider: {
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
    },
    areaServed: {
      "@type": "City",
      name: "Saskatoon",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.saskroofingcompany.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: "https://www.saskroofingcompany.com/areas",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Saskatoon",
        item: "https://www.saskroofingcompany.com/areas/saskatoon",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Roof Repair",
        item: "https://www.saskroofingcompany.com/areas/saskatoon/repair",
      },
    ],
  };

  return (
    <>
      <SEO
        title="Roof Repair Saskatoon | Sask Roofing"
        description="Expert roof repair in Saskatoon, SK. We fix leaks, missing shingles, and ice dam damage quickly. Free diagnosis and honest recommendations. 4.9★ rated."
        schema={[
          faqSchema,
          localBusinessSchema,
          serviceSchema,
          breadcrumbSchema,
        ]}
      />
      <div className="flex flex-col min-h-screen">
        <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
              alt="Saskatoon Roof Repair"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                Roof Repair in Saskatoon
              </h1>
              <p className="text-xl text-slate-300 leading-relaxed">
                Don't let a small leak turn into a major structural disaster. We
                provide fast, accurate diagnostics and reliable roof repairs
                across Saskatoon and surrounding communities. Honest pricing and
                expert local crews.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">Request a Roof Repair</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold">
                  When You Need Roof Repair in Saskatoon
                </h2>
                <p className="text-lg text-muted-foreground">
                  Saskatoon's weather is relentless. High winds on the prairies
                  and extreme temperature swings can compromise even the best
                  roofs. Catching damage early is the key to avoiding a full
                  replacement.
                </p>
                <div className="space-y-4 pt-4">
                  <h3 className="text-xl font-semibold">
                    Watch for these critical warning signs:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Clock className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Water Stains:</strong> Brown or yellow rings on
                        your ceilings or walls are a sure sign of an active
                        leak.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Shield className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Missing Shingles:</strong> High winds can tear
                        shingles away, leaving your decking exposed to rot.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Wrench className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Damaged Flashing:</strong> Metal around
                        chimneys, vents, and valleys is a common entry point for
                        water.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <DollarSign className="w-6 h-6 text-primary shrink-0" />
                      <span>
                        <strong>Ice Damming:</strong> Thick ice at the eaves
                        indicates ventilation issues that will lead to winter
                        leaks.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                  alt="Saskatoon roof leak repair"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-muted/30">
          <div className="container px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
              <h2 className="text-3xl font-bold">
                Local Expertise for Saskatoon's Climate
              </h2>
              <p className="text-lg text-muted-foreground">
                We understand how Saskatoon's weather affects different roofing
                materials. Our repairs are engineered to withstand the extreme
                prairie elements.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">
                  Wind Damage Specialists
                </h3>
                <p className="text-muted-foreground">
                  We use enhanced nailing patterns and high-wind rated sealants
                  to ensure your shingles stay put during the next prairie gale.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Ice Dam Solutions</h3>
                <p className="text-muted-foreground">
                  We don't just fix the leak; we assess your attic's insulation
                  and ventilation to prevent ice dams from returning next
                  winter.
                </p>
              </div>
              <div className="bg-background p-8 rounded-xl border shadow-sm">
                <h3 className="text-xl font-bold mb-4">Cold-Weather Repairs</h3>
                <p className="text-muted-foreground">
                  We use specialized sealants and techniques that allow us to
                  perform effective, permanent repairs even in sub-zero
                  temperatures.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-background">
          <div className="container px-4">
            <div className="grid md:grid-cols-2 gap-8 mb-20">
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Building Permits and Code in Saskatoon
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  In Saskatoon, standard maintenance and minor roof repairs
                  (such as replacing individual shingles or fixing flashing) do
                  not require a building permit. However, if the repair involves
                  structural modifications or the replacement of structural
                  components like trusses or large sections of the roof deck, a
                  permit might be necessary. We handle all assessments of your
                  roof's condition and will advise you if your repair project
                  triggers any municipal requirements. Our goal is to ensure
                  your repair is not only effective but also fully compliant
                  with all local Saskatoon building bylaws.
                </p>
              </div>
              <div className="bg-muted/30 p-8 rounded-xl border shadow-sm border-l-4 border-primary">
                <h3 className="text-2xl font-bold mb-4">
                  Insurance and Storm Claims in Saskatoon
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  If your roof has been damaged by a specific weather event like
                  a hailstorm or high wind, your homeowner's insurance likely
                  covers the cost of the repair. We specialize in documenting
                  storm damage for insurance adjusters in Saskatoon. Before you
                  file a claim, call us for a free inspection. We will provide a
                  detailed report with photos of the damage, which helps ensure
                  your claim is processed accurately and that you receive the
                  full amount needed to restore your roof to its original
                  condition.
                </p>
              </div>
            </div>

            <div className="mb-20">
              <div className="bg-card p-8 rounded-xl border shadow-sm max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold mb-4 text-center">
                  Materials and Warranties for Saskatoon Roofs
                </h3>
                <p className="text-muted-foreground leading-relaxed text-center mb-6">
                  For every repair in Saskatoon, we use only the highest-quality
                  materials that are rated for our extreme climate. We carry a
                  wide inventory of architectural shingles to ensure the best
                  possible color match for your existing roof. All our repair
                  work is backed by our local workmanship warranty, giving you
                  peace of mind that the fix is permanent and that your home is
                  protected against the next round of Saskatchewan weather.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      24-48hr
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Response Time
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      100%
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Color Match Goal
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Cold-Rated
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Sealants Used
                    </span>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <span className="block text-2xl font-bold text-primary mb-1">
                      Local
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Saskatoon Crew
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60"
                  alt="Saskatoon roof repair work"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <h2 className="text-3xl font-bold">
                  Our Saskatoon Roof Repair Process
                </h2>
                <p className="text-lg text-muted-foreground">
                  We make getting your roof fixed simple and stress-free, with
                  clear communication and transparent pricing.
                </p>
                <ol className="space-y-6 pt-4">
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Diagnostic Inspection
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We find the exact source of the leak, which is often far
                        from where the water is visible inside.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Transparent Quote
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        You get a fixed-price written quote. We explain exactly
                        what needs to be done and why.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">Quality Repair</h4>
                      <p className="text-muted-foreground mt-1">
                        Our local crew performs the repair using premium
                        materials and cold-weather grade sealants.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="text-xl font-semibold">
                        Final Verification
                      </h4>
                      <p className="text-muted-foreground mt-1">
                        We test the repair and clear any debris, leaving your
                        property as clean as we found it.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-muted/10 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label="Saskatoon roofing pages"
              className="bg-background p-6 rounded-xl border shadow-sm"
            >
              <p className="font-bold text-lg mb-4 text-foreground">
                More roofing services in Saskatoon:
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/areas/saskatoon"
                    className="text-primary hover:underline font-medium"
                  >
                    ← All Saskatoon roofing services (overview)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/areas/saskatoon/replacement"
                    className="text-primary hover:underline font-medium"
                  >
                    Roof Replacement in Saskatoon
                  </Link>
                </li>
                <li>
                  <Link
                    to="/services/repair"
                    className="text-primary hover:underline font-medium"
                  >
                    All Sask roofing services
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </section>

        <section className="py-20 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Saskatoon Roof Repair FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-background rounded-xl border p-2 shadow-sm"
            >
              <AccordionItem value="item-1" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How much does roof repair cost in Saskatoon?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Minor repairs in Saskatoon typically range from $300 to $800,
                  while major repairs can range from $1,000 to $3,000. We
                  provide fixed-price quotes before any work begins.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  How quickly can you fix a leaking roof in Saskatoon?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  We prioritize active leaks and storm damage. In most cases, we
                  can have a repair crew out within 24-48 hours to secure your
                  property and stop the water ingress.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-b-0 px-4">
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  Do you offer emergency roof repairs in the winter?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6">
                  Yes, we provide year-round emergency roof repairs in
                  Saskatoon, including during the winter months when ice damming
                  is most prevalent.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        <section className="py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Stop the Leak Today
            </h2>
            <p className="text-xl opacity-90">
              Get a free diagnosis and a fixed-price quote from Saskatoon's
              trusted roof repair experts.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Request Your Free Diagnosis</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
